import { useState, useCallback, useRef, useMemo } from 'react'

const SCORE_TYPES = {
  POSITIVE: 1,
  NEGATIVE: -1,
  NEUTRAL: 0,
}

const useItemReviews = (
  initialComments = [],
  initialRatings = [],
  initialCommentsCount = 0,
  initialLikeCount = 0,
  initialDislikeCount = 0,
  currentUserName = null,
) => {
  const [comments, setComments] = useState(initialComments)
  const [ratings, setRatings] = useState(initialRatings)
  const replacedReviewRef = useRef(null) // Store single replaced review for rollback

  const addReviewOptimistic = useCallback((newReview) => {
    const reviewWithTimestamp = {
      ...newReview,
      anonymous: newReview.anonymous,
      updatedAt: new Date().toISOString(),
    }

    if (newReview.type === 'comment') {
      // Comments can be unlimited per user
      setComments(prev => [reviewWithTimestamp, ...prev])
    } else if (newReview.type === 'review') {
      // Reviews (ratings) should be one per user - replace existing if found
      setRatings(prev => {
        const userReviewIndex = prev.findIndex(rating => {
          if (currentUserName) {
            const existingIsAnonymous = rating.anonymous
            const newIsAnonymous = reviewWithTimestamp.anonymous
            const existingActualUsername = existingIsAnonymous ? currentUserName : rating.username
            const newActualUsername = newIsAnonymous ? currentUserName : reviewWithTimestamp.username

            return existingActualUsername === newActualUsername
          }

          return rating.username === reviewWithTimestamp.username
        })

        if (userReviewIndex !== -1) {
          // Store the original review for potential rollback
          const originalReview = prev[userReviewIndex]
          replacedReviewRef.current = {
            tempId: reviewWithTimestamp.id,
            original: originalReview,
          }

          // Replace existing review from the same user
          const updatedRatings = [...prev]
          updatedRatings[userReviewIndex] = reviewWithTimestamp
          return updatedRatings
        }
        // Add new review if no existing review from this user
        return [reviewWithTimestamp, ...prev]
      })
    }
  }, [currentUserName])

  const removeReviewOptimistic = useCallback((reviewId, reviewType) => {
    if (reviewType === 'comment') {
      setComments(prev => prev.filter(comment => comment.id !== reviewId))
    } else if (reviewType === 'review') {
      // Check if this was a replacement and restore original if so
      const replacementData = replacedReviewRef.current

      if (replacementData && replacementData.tempId === reviewId) {
        // This was a replacement, restore the original review
        setRatings(prev => {
          const updatedRatings = prev.filter(rating => rating.id !== reviewId)
          return [replacementData.original, ...updatedRatings]
        })
        // Clean up the replacement tracking
        replacedReviewRef.current = null
      } else {
        // This was a new review, just remove it
        setRatings(prev => prev.filter(rating => rating.id !== reviewId))
      }
    }
  }, [])

  // Calculate dynamic counts including optimistic updates
  const counts = useMemo(() => {
    // Count optimistic comments (those with timestamp-like IDs > 1000000000000)
    const optimisticComments = comments.filter(comment =>
      typeof comment.id === 'number' && comment.id > 1000000000000
    ).length

    // For ratings: initialLikeCount/initialDislikeCount = total counts from ALL ratings in DB
    // initialRatings = only first page of ratings
    // We need to calculate net change from optimistic updates only
    let netPositiveChange = 0
    let netNegativeChange = 0

    // Check if current user has an optimistic review (replacement)
    const optimisticUserReview = ratings.find(rating => {
      // Must be optimistic AND belong to current user
      if (!(typeof rating.id === 'number' && rating.id > 1000000000000)) {
        return false
      }

      if (currentUserName) {
        const ratingIsAnonymous = rating.anonymous
        const ratingActualUsername = ratingIsAnonymous ? currentUserName : rating.username
        return ratingActualUsername === currentUserName
      }

      return false
    })

    if (optimisticUserReview) {
      // User has posted an optimistic review
      // Check if they had an original review in the initial data
      const originalUserReview = initialRatings.find(rating => {
        if (currentUserName) {
          const ratingIsAnonymous = rating.anonymous
          const ratingActualUsername = ratingIsAnonymous ? currentUserName : rating.username
          return ratingActualUsername === currentUserName
        }
        return false
      })

      // Calculate net change: remove original contribution, add new contribution
      if (originalUserReview) {
        // User had an existing review - this is a replacement
        if (originalUserReview.score === SCORE_TYPES.POSITIVE) {
          netPositiveChange -= 1
        } else if (originalUserReview.score === SCORE_TYPES.NEGATIVE) {
          netNegativeChange -= 1
        }
      }

      // Add new review contribution
      if (optimisticUserReview.score === SCORE_TYPES.POSITIVE) {
        netPositiveChange += 1
      } else if (optimisticUserReview.score === SCORE_TYPES.NEGATIVE) {
        netNegativeChange += 1
      }
    }

    return {
      comments: initialCommentsCount + optimisticComments,
      positive: initialLikeCount + netPositiveChange,
      negative: initialDislikeCount + netNegativeChange,
    }
  }, [comments, ratings, initialCommentsCount, initialLikeCount, initialDislikeCount, initialRatings, currentUserName])

  return {
    comments,
    ratings,
    counts,
    addReviewOptimistic,
    removeReviewOptimistic,
  }
}

export default useItemReviews
